/**
 * Hydrology lakes (terrain + water realism wave) — player-following regional
 * bakes: bed heights → priority-flood depression fill (enginex
 * TerrainHydrology) → lake surfaces where `filled − raw` exceeds a minimum
 * depth. Local pits pool at ANY elevation, so water shows up near spawn
 * (pin plains, wetlands) without moving terrain heights; the remote
 * below-sea-level ocean stays the AtlasWaterModule sea layer's job.
 *
 * Deterministic per (seed, region): the fill uses only comparisons and
 * addition — bit-identical on every client. Lake cells inside a settlement
 * footprint are cleared so no seeded town ever floods.
 *
 * The bake is DRIVEN by AtlasWaterModule (sliced rows per frame); completed
 * regions land in a small cache other systems query via lakeSurfaceAtBaked
 * (foliage skips plants under lake water).
 */
import { meshHeight } from './terrain_stamp.js';
import { settlementSites, SETTLEMENT_FOOTPRINT } from '@shared/settlements.js';
import { riverWaterAt, riverStillAt, plateauSites } from '@shared/terrain.js';
import { fillDepressions } from 'enginex/terrain/TerrainHydrology.js';
import { commonsSeaLevel } from './sea_level.js';
import { FREEBOARD } from './hydrology_bake.js';

/** Region footprint (m) baked around the player. */
export const LAKE_REGION_SIZE = 1536;
/** Grid nodes per side (8 m cells). */
export const LAKE_GRID_N = 257; // 6 m cells (was 8) — lake rims lose the faceted look
/** Minimum pooled depth (m) for a cell to count as lake. */
const MIN_DEPTH = 0.35;
/** Beyond a settlement's plateau disc that stays dry whatever a baked lake's level (the blend ring below it may be shore). */
export const PLATEAU_DRY_M = 10;
/** Minimum component area in cells (drops one-cell puddle spam). */
const MIN_CELLS = 6;
/**
 * Maximum component depth (m). A REGIONAL fill can't know continental
 * drainage: a low plate ringed by higher ground inside the window (the
 * city pin!) reads as a closed mega-basin and floods tens of metres deep.
 * Genuine ponds are shallow pits — anything deeper is a boundary artifact.
 */
const MAX_COMP_DEPTH = 5;

/**
 * Baked lakes (terrain wave 2026-09-08) — the world hydrology bake's basin
 * list per seed (centroid, level, depth, bbox), registered by hydrology_load
 * on every thread. A regional component that holds a baked lake's centroid
 * takes the BAKED level (the continental spill, which a 1.5 km window cannot
 * see) and skips the depth cap; a breached basin's 12 m floor lake and a
 * lake wider than the window both render at their true level.
 */
export interface BakedLake { cx: number; cz: number; level: number; depth: number; x0: number; z0: number; x1: number; z1: number }
const bakedLakes = new Map<string, BakedLake[]>();
export function setBakedLakes(seed: string, lakes: BakedLake[] | null): void {
  if (!lakes) bakedLakes.delete(seed); else bakedLakes.set(seed, lakes);
}
/** The baked lakes as the SERVER's water table holds them (flat [x0, z0, x1, z1, level × 10], the bake's rounding —
 *  client/verify/bake-water-index.mts): the fishing cast asks the server's own lake rule with these numbers
 *  (shared/water_rules.ts), so an offered cast is one the server accepts. Cached per registered list. */
const lakeRows = new WeakMap<BakedLake[], Float64Array>();
export function bakedLakeRows(seed: string): Float64Array {
  const all = bakedLakes.get(seed);
  if (!all) return new Float64Array(0);
  let rows = lakeRows.get(all);
  if (!rows) {
    rows = new Float64Array(all.length * 5);
    all.forEach((l, i) => { rows![i * 5] = Math.floor(l.x0); rows![i * 5 + 1] = Math.floor(l.z0); rows![i * 5 + 2] = Math.ceil(l.x1); rows![i * 5 + 3] = Math.ceil(l.z1); rows![i * 5 + 4] = Math.round(l.level * 10); });
    lakeRows.set(all, rows);
  }
  return rows;
}
export function bakedLakesNear(seed: string, cx: number, cz: number, half: number): BakedLake[] {
  const all = bakedLakes.get(seed);
  if (!all) return [];
  return all.filter((l) => l.x1 >= cx - half && l.x0 <= cx + half && l.z1 >= cz - half && l.z0 <= cz + half);
}
/**
 * Lakes must sit ABOVE the sea. Seabed depressions also fill in a regional
 * pass, producing water-on-water sheets that breach or refract through the
 * sea surface as hard 8 m-grid squares (the "square geometry on the water"
 * bug) — the ocean layer owns everything at/below sea level.
 */
const SEA_MARGIN_M = 2;
/** Completed regions kept for queries (player rarely straddles more). */
const CACHE_MAX = 4;

export interface LakeRegion {
  /** Region CENTER in world coordinates (snapped). */
  centerX: number;
  centerZ: number;
  /** Bed heights, row-major LAKE_GRID_N² (row = z). */
  raw: Float32Array;
  /** Lake surface height per node; NaN where dry. */
  surface: Float32Array;
  /** Deepest pooled water in the region (0 = no lakes). */
  maxDepth: number;
}

const cache = new Map<string, LakeRegion>();

export function lakeRegionKey(centerX: number, centerZ: number): string {
  return `${centerX},${centerZ}`;
}

/**
 * Insert a WORKER-baked region into THIS thread's cache so queries
 * (foliage skip, underwater checks) see it — the water bake worker runs
 * createLakeBake in its own module instance with its own cache.
 */
export function insertLakeRegion(region: LakeRegion): void {
  cache.set(lakeRegionKey(region.centerX, region.centerZ), region);
  while (cache.size > CACHE_MAX) {
    cache.delete(cache.keys().next().value as string);
  }
}

/** A completed region covering world (x,z), if any is cached. */
function regionCovering(x: number, z: number): LakeRegion | null {
  const half = LAKE_REGION_SIZE / 2;
  for (const r of cache.values()) {
    if (Math.abs(x - r.centerX) <= half && Math.abs(z - r.centerZ) <= half) return r;
  }
  return null;
}

/**
 * Lake surface height at world (x,z) from the BAKED regions, or null when
 * dry / not yet baked. Nearest-node lookup — callers use it for coarse
 * gates (foliage skip, underwater check), not for rendering.
 */
export function lakeSurfaceAtBaked(x: number, z: number): number | null {
  const r = regionCovering(x, z);
  if (!r) return null;
  const n = LAKE_GRID_N;
  const col = Math.round(((x - r.centerX) / LAKE_REGION_SIZE + 0.5) * (n - 1));
  const row = Math.round(((z - r.centerZ) / LAKE_REGION_SIZE + 0.5) * (n - 1));
  if (col < 0 || col >= n || row < 0 || row >= n) return null;
  const s = r.surface[row * n + col];
  return Number.isNaN(s) ? null : s;
}

/**
 * Surface height of the wet lake node NEAREST to world (x, z) in a baked
 * region, or null when the region holds no lake. Coarse (every `stride`-th
 * node): it seats the lakes' planar-reflection mirror plane, which wants the
 * level of the lake the camera is looking at, not a 6 m-exact answer. Pure —
 * verify:water-reflections pins it.
 */
export function nearestLakeLevel(region: LakeRegion, x: number, z: number, stride = 4): number | null {
  const n = LAKE_GRID_N;
  const cell = LAKE_REGION_SIZE / (n - 1);
  const cx = (x - region.centerX) / cell + (n - 1) / 2;
  const cz = (z - region.centerZ) / cell + (n - 1) / 2;
  const step = Math.max(1, Math.floor(stride));
  let best = Infinity;
  let level: number | null = null;
  for (let row = 0; row < n; row += step) {
    const dz = row - cz;
    for (let col = 0; col < n; col += step) {
      const s = region.surface[row * n + col];
      if (Number.isNaN(s)) continue;
      const dx = col - cx;
      const d = dx * dx + dz * dz;
      if (d < best) { best = d; level = s; }
    }
  }
  return level;
}

/** Incremental region bake: fill rows across frames, then finalize(). */
export interface LakeBake {
  readonly centerX: number;
  readonly centerZ: number;
  /** Sample bed rows [from, to); returns the next row index. */
  fillRows(from: number, to: number): number;
  /** Flood-fill + extract lakes; caches and returns the region. */
  finalize(): LakeRegion;
}

export function createLakeBake(seed: string, centerX: number, centerZ: number): LakeBake {
  const n = LAKE_GRID_N;
  const raw = new Float32Array(n * n);

  return {
    centerX,
    centerZ,
    fillRows(from: number, to: number): number {
      const end = Math.min(n, to);
      for (let row = from; row < end; row++) {
        const z = (row / (n - 1) - 0.5) * LAKE_REGION_SIZE + centerZ;
        for (let col = 0; col < n; col++) {
          const x = (col / (n - 1) - 0.5) * LAKE_REGION_SIZE + centerX;
          // Rendered truth — pools settle on VISIBLE basin rims. A river channel is
          // a trench in that truth, but a basin spills through it at the channel's
          // WATER level, not its bed (terrain wave 2026-09-08): cap the bed at the
          // water surface so the fill and the river ribbon agree.
          const h = meshHeight(seed, x, z);
          const rw = riverWaterAt(seed, x, z);
          // A basin spills through its outlet channel at the channel's WATER
          // surface, not its bed — so channel cells are capped at the water.
          // But a channel ON a lake (a still-water reach) is lake floor: capped,
          // the river became a ridge at the spill level across the lake floor,
          // the growth step could not cross it (raw ≥ level − MIN_DEPTH) and
          // every pit beyond the river fell to the 5 m depth cap — the lake the
          // user stood on the floor of, dry, with the river hanging over it.
          raw[row * n + col] = rw !== null && rw > h && !riverStillAt(seed, x, z) ? rw : h;
        }
      }
      return end;
    },

    finalize(): LakeRegion {
      const seaLevel = commonsSeaLevel(seed);
      // Pad with a virtual drain ring far below any terrain so the window
      // BORDER itself is never a dam — pits whose real spill lies outside
      // the window under-fill (conservative) instead of rim-locking.
      const np = n + 2;
      const padded = new Float32Array(np * np).fill(-1e6);
      for (let row = 0; row < n; row++) {
        padded.set(raw.subarray(row * n, row * n + n), (row + 1) * np + 1);
      }
      const filledPadded = fillDepressions(padded, np);
      const filled = new Float32Array(n * n);
      for (let row = 0; row < n; row++) {
        filled.set(filledPadded.subarray((row + 1) * np + 1, (row + 1) * np + 1 + n), row * n);
      }

      const surface = new Float32Array(n * n).fill(NaN);
      const nearBaked = bakedLakesNear(seed, centerX, centerZ, LAKE_REGION_SIZE / 2 + 200);
      const wet = new Uint8Array(n * n);
      for (let i = 0; i < raw.length; i++) {
        if (filled[i] - raw[i] > MIN_DEPTH) wet[i] = 1;
      }

      // Clear lake cells inside settlement footprints — towns never flood.
      // THE DRY PLATEAU LAW (2026-09-23): the protected disc is the settlement's
      // whole PLATEAU (+ `PLATEAU_DRY_M`), not 1.1 × its footprint — a city's
      // plateau is 440 m against a 264 m footprint — and the mask is kept
      // (`dry`) so the baked-lake GROWTH below honours it too: that growth
      // walked `raw` alone, and a baked lake whose level stands above a
      // neighbouring plateau (13 sites; the bake bumps plateaus +60 m, so a
      // lake may be baked right up to a town's rim) flooded the town's disc
      // through its blend ring (the disc is flat and under the level). The
      // ring's lower slope may still be shore — a lakeside town.
      const dry = new Uint8Array(n * n);
      const plateauR = new Map<number, number>();
      for (const p of plateauSites(seed)) plateauR.set(p.id, p.r);
      for (const site of settlementSites(seed)) {
        const rad = Math.max((SETTLEMENT_FOOTPRINT[site.tier] ?? 120) * 1.1, (plateauR.get(site.id) ?? 0) + PLATEAU_DRY_M);
        // Skip a site only when its WHOLE dry disc misses the window (2026-09-25): the fixed `half + 200` (968 m) fitted
        // the old 1.1 × footprint radius — a city's 450 m plateau 1.0–1.2 km from the window centre and a capital's
        // ~3.5 km plateau more than ~1 km out lost the part inside the window, in the wet clear AND the growth's `dry`.
        const reach = LAKE_REGION_SIZE / 2 + rad;
        if (Math.abs(site.centerX - centerX) > reach || Math.abs(site.centerZ - centerZ) > reach) continue;
        const cell = LAKE_REGION_SIZE / (n - 1);
        const c0 = Math.max(0, Math.floor(((site.centerX - rad - centerX) / LAKE_REGION_SIZE + 0.5) * (n - 1)));
        const c1 = Math.min(n - 1, Math.ceil(((site.centerX + rad - centerX) / LAKE_REGION_SIZE + 0.5) * (n - 1)));
        const r0 = Math.max(0, Math.floor(((site.centerZ - rad - centerZ) / LAKE_REGION_SIZE + 0.5) * (n - 1)));
        const r1 = Math.min(n - 1, Math.ceil(((site.centerZ + rad - centerZ) / LAKE_REGION_SIZE + 0.5) * (n - 1)));
        const rad2 = rad * rad;
        for (let row = r0; row <= r1; row++) {
          const wz = (row / (n - 1) - 0.5) * LAKE_REGION_SIZE + centerZ;
          for (let col = c0; col <= c1; col++) {
            const wx = (col / (n - 1) - 0.5) * LAKE_REGION_SIZE + centerX;
            if ((wx - site.centerX) ** 2 + (wz - site.centerZ) ** 2 < rad2) { wet[row * n + col] = 0; dry[row * n + col] = 1; }
          }
        }
        void cell;
      }

      // Connected components (4-neighbour BFS); drop puddles < MIN_CELLS.
      const label = new Int32Array(n * n).fill(-1);
      const queue: number[] = [];
      let next = 0;
      let maxDepth = 0;
      for (let i = 0; i < wet.length; i++) {
        if (!wet[i] || label[i] >= 0) continue;
        const id = next++;
        const members: number[] = [];
        queue.length = 0;
        queue.push(i);
        label[i] = id;
        while (queue.length > 0) {
          const c = queue.pop() as number;
          members.push(c);
          const cx = c % n;
          const cz = (c / n) | 0;
          const neighbours = [
            cx + 1 < n ? c + 1 : -1,
            cx - 1 >= 0 ? c - 1 : -1,
            cz + 1 < n ? c + n : -1,
            cz - 1 >= 0 ? c - n : -1,
          ];
          for (const nb of neighbours) {
            if (nb >= 0 && wet[nb] && label[nb] < 0) {
              label[nb] = id;
              queue.push(nb);
            }
          }
        }
        if (members.length < MIN_CELLS) continue;
        // Depth cap: rim-locked mega-basins (see MAX_COMP_DEPTH) are dropped
        // whole — only genuine shallow pools render. Sea guard: any component
        // whose surface reaches the sea band belongs to the ocean layer.
        let compDepth = 0;
        let compSurface = -Infinity;
        let mx0 = Infinity, mx1 = -Infinity, mz0 = Infinity, mz1 = -Infinity;
        for (const m of members) {
          const d = filled[m] - raw[m];
          if (d > compDepth) compDepth = d;
          if (filled[m] > compSurface) compSurface = filled[m];
          const wx = ((m % n) / (n - 1) - 0.5) * LAKE_REGION_SIZE + centerX;
          const wz = (((m / n) | 0) / (n - 1) - 0.5) * LAKE_REGION_SIZE + centerZ;
          if (wx < mx0) mx0 = wx; if (wx > mx1) mx1 = wx; if (wz < mz0) mz0 = wz; if (wz > mz1) mz1 = wz;
        }
        // A baked lake whose centroid lies in this component's bbox sets the level.
        const baked = nearBaked.find((l) => l.cx >= mx0 - 3 && l.cx <= mx1 + 3 && l.cz >= mz0 - 3 && l.cz <= mz1 + 3);
        if (!baked && compDepth > MAX_COMP_DEPTH) continue;
        if (compSurface <= seaLevel + SEA_MARGIN_M) continue;
        if (baked) {
          // The baked level, grown to every connected cell under it (a window
          // border may have under-filled the fill's own component).
          // The level: the fill's own spill when the window holds the whole
          // basin (the outlet channel's water — the lake's true surface), else
          // the baked spill minus the free-board a 1.5 km window cut below
          // (`min(spill, baked)` alone under-filled a cut basin by metres and
          // left the river crossing it a metre in the air, 2026-09-11); never
          // above the baked level.
          const level = Math.min(baked.level, Math.max(compSurface, baked.level - FREEBOARD));
          const grow: number[] = [];
          for (const m of members) if (raw[m] < level - MIN_DEPTH) { grow.push(m); }
          const seenG = new Uint8Array(n * n);
          for (const m of grow) seenG[m] = 1;
          while (grow.length) {
            const c = grow.pop() as number;
            surface[c] = level;
            const d = level - raw[c];
            if (d > maxDepth) maxDepth = d;
            const cx = c % n, cz = (c / n) | 0;
            const nbs = [cx + 1 < n ? c + 1 : -1, cx - 1 >= 0 ? c - 1 : -1, cz + 1 < n ? c + n : -1, cz - 1 >= 0 ? c - n : -1];
            for (const nb of nbs) if (nb >= 0 && !seenG[nb] && !dry[nb] && raw[nb] < level - MIN_DEPTH) { seenG[nb] = 1; grow.push(nb); }
          }
          continue;
        }
        for (const m of members) {
          surface[m] = filled[m];
          const d = filled[m] - raw[m];
          if (d > maxDepth) maxDepth = d;
        }
      }

      const region: LakeRegion = { centerX, centerZ, raw, surface, maxDepth };
      cache.set(lakeRegionKey(centerX, centerZ), region);
      // Small LRU: evict the oldest entries beyond the cap.
      while (cache.size > CACHE_MAX) {
        const oldest = cache.keys().next().value as string;
        cache.delete(oldest);
      }
      return region;
    },
  };
}
